import React, { useEffect, useState } from 'react';
import CompactForm, { FormField } from '../ui/Form/CompactForm';
import FormFieldGroup from '../ui/Form/FormFieldGroup';
import CollapsibleSection from '../ui/Section/CollapsibleSection';
import ResponsiveModal from '../ui/Modal/ResponsiveModal';
import { useDebounce } from '../../hooks/useDebounce';
import { apiService } from '../../services/api';
import { formatDealType, isFinancedDeal } from '../../utils/dealType';

/**
 * Fields a dealer can correct on an existing deal. Bound to the BACKEND
 * DealUpdate schema names (interest_rate / down_payment_amount / amount_financed),
 * NOT the legacy frontend aliases.
 *
 * amount_financed and monthly_payment are DERIVED, not free input. Financed
 * Amount is a plain client-side sum of the form's own price/down/tax/fees
 * fields (see computeFinanced). Monthly Payment (WS3 Task 8) comes from the
 * canonical engine via a debounced POST /deals/calculate call — the browser no
 * longer runs its own amortization formula. Either way, the server never
 * trusts these values: api/v1/endpoints/deals.py::update_deal always
 * recomputes both through services.deal_persistence.persist_deal_financials
 * on save, so the deal the dealer sees always agrees with the loan that gets
 * serviced. APR and credit_tier are independent — the loan reads
 * deal.interest_rate directly (no tier lookup).
 */
export interface EditableDeal {
  deal_status?: string;
  purchase_price?: number;
  total_vehicle_cost?: number;
  front_gross?: number;
  backend_gross?: number;
  total_gross?: number;
  down_payment_amount?: number;
  amount_financed?: number;
  interest_rate?: number;
  term_months?: number;
  monthly_payment?: number;
  sales_tax?: number;
  total_fees?: number;
  lender_name?: string;
  credit_score?: number;
  credit_tier?: string;
  deal_score?: string;
}

interface DealContext extends EditableDeal {
  deal_number: string;
  deal_type: string;
}

interface EditDealModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EditableDeal) => Promise<void>;
  deal: DealContext | null;
  loading?: boolean;
}

// Manual sales-pipeline statuses a dealer sets by hand. CLOSED and
// PENDING_FUNDING are system-managed (close-to-bhph / finalize) and excluded
// — but if the deal already sits in one, keep it selectable so we never blank it.
const MANUAL_STATUSES = ['DRAFT', 'PENDING', 'SOLD', 'CANCELLED'];

const CREDIT_TIERS = [
  { value: '', label: 'Select tier…' },
  { value: 'prime', label: 'Prime' },
  { value: 'near_prime', label: 'Near Prime' },
  { value: 'subprime', label: 'Subprime' },
  { value: 'deep_subprime', label: 'Deep Subprime' },
  { value: 'special_finance', label: 'Special Finance' },
];

const DEAL_SCORES = [
  { value: '', label: 'Select…' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];

// User-editable numeric inputs (amount_financed + monthly_payment are derived,
// so they are NOT in this list).
const numberFields: (keyof EditableDeal)[] = [
  'purchase_price',
  'total_vehicle_cost',
  'front_gross',
  'backend_gross',
  'total_gross',
  'down_payment_amount',
  'interest_rate',
  'term_months',
  'sales_tax',
  'total_fees',
  'credit_score',
];

const toFormValue = (v: number | string | undefined): string =>
  v === undefined || v === null ? '' : String(v);

const num = (v: string | undefined): number => {
  const n = parseFloat(v ?? '');
  return Number.isNaN(n) ? 0 : n;
};

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

const formatCurrency = (n: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

/**
 * Financed Amount is a plain sum of the form's own displayed price/down/tax/
 * fees fields — a quick sanity-check total, not a re-derivation of the
 * canonical engine (those fields already reflect whatever the server last
 * computed, or a dealer's manual correction on an imported deal).
 */
const computeFinanced = (form: Record<string, string>) => {
  const purchase = num(form.purchase_price);
  const down = num(form.down_payment_amount);
  const tax = num(form.sales_tax);
  const fees = num(form.total_fees);
  const financed = Math.max(0, purchase - down + tax + fees);
  return {
    financed: round2(financed),
    // Well-defined from price/down/tax/fees alone. Most imported deals have
    // term_months=0, so Financed must not depend on a term being present.
    financedComputable: purchase > 0,
  };
};

/**
 * WS3 Task 8: Monthly Payment used to be re-derived client-side with the same
 * loan-amortization formula the BHPH loan uses (services/account_lifecycle_
 * service.py:139-161), duplicating the canonical engine. It now comes from the
 * ONE preview endpoint (POST /deals/calculate) — the SAME engine call
 * services.deal_persistence.persist_deal_financials makes when this edit is
 * saved, so the number shown here is the number that will actually persist
 * (modulo trade-in/payment-frequency, which this edit surface doesn't expose —
 * see the frequency note on the request below).
 */
const monthlyComputable = (form: Record<string, string>): boolean => {
  const purchase = num(form.purchase_price);
  const term = Math.trunc(num(form.term_months));
  return purchase > 0 && term > 0;
};

const EditDealModal: React.FC<EditDealModalProps> = ({
  isOpen,
  onClose,
  onSave,
  deal,
  loading = false,
}) => {
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [monthlyPreview, setMonthlyPreview] = useState<number | null>(null);
  const [monthlyPreviewLoading, setMonthlyPreviewLoading] = useState(false);

  useEffect(() => {
    if (!deal) return;
    setForm({
      deal_status: deal.deal_status || 'PENDING',
      purchase_price: toFormValue(deal.purchase_price),
      total_vehicle_cost: toFormValue(deal.total_vehicle_cost),
      front_gross: toFormValue(deal.front_gross),
      backend_gross: toFormValue(deal.backend_gross),
      total_gross: toFormValue(deal.total_gross),
      down_payment_amount: toFormValue(deal.down_payment_amount),
      interest_rate: toFormValue(deal.interest_rate),
      term_months: toFormValue(deal.term_months),
      sales_tax: toFormValue(deal.sales_tax),
      total_fees: toFormValue(deal.total_fees),
      lender_name: deal.lender_name || '',
      credit_score: toFormValue(deal.credit_score),
      credit_tier: deal.credit_tier || '',
      deal_score: deal.deal_score || '',
    });
    setError(null);
    setMonthlyPreview(null);
  }, [deal, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Debounced Monthly Payment preview — the ONE call to the canonical engine
  // (POST /deals/calculate), replacing the local exponentiation-based amortization. Only
  // fires once purchase_price + term_months + interest_rate are all present
  // (mirrors monthlyComputable) so an incomplete/imported deal never 400s.
  const isFinancedDealType = !!deal && isFinancedDeal(deal.deal_type);
  const debouncedPreviewKey = useDebounce(
    isFinancedDealType && monthlyComputable(form)
      ? `${form.purchase_price}|${form.down_payment_amount}|${form.interest_rate}|${form.term_months}|${deal?.deal_type}`
      : null,
    400
  );

  useEffect(() => {
    if (!debouncedPreviewKey || !deal) {
      setMonthlyPreview(null);
      return;
    }
    let cancelled = false;
    setMonthlyPreviewLoading(true);
    apiService
      .post<{ payment_amount: number }>('/deals/calculate', {
        vehicle_price: num(form.purchase_price),
        down_payment: num(form.down_payment_amount),
        apr: num(form.interest_rate),
        term_periods: Math.trunc(num(form.term_months)),
        // This edit surface doesn't expose payment frequency (it lives on the
        // deal's worksheet) -- monthly is the best-effort preview basis. The
        // actual Save always recomputes server-side using the worksheet's real
        // frequency, so this only affects the live preview, never the persist.
        frequency: 'monthly',
        deal_type: deal.deal_type,
      })
      .then((preview) => {
        if (!cancelled) setMonthlyPreview(round2(preview.payment_amount));
      })
      .catch(() => {
        if (!cancelled) setMonthlyPreview(null);
      })
      .finally(() => {
        if (!cancelled) setMonthlyPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    debouncedPreviewKey,
    deal,
    form.purchase_price,
    form.down_payment_amount,
    form.interest_rate,
    form.term_months,
  ]);

  const buildPayload = (): EditableDeal => {
    const payload: EditableDeal = {};
    payload.deal_status = form.deal_status;
    // Integer columns on the backend → truncate.
    const intFields = new Set(['term_months', 'credit_score']);
    for (const field of numberFields) {
      const raw = form[field];
      if (raw !== undefined && raw !== '') {
        const n = parseFloat(raw);
        if (!Number.isNaN(n)) {
          (payload as Record<string, number>)[field] = intFields.has(field) ? Math.trunc(n) : n;
        }
      }
    }
    // Derived financials: persist so the deal-detail display matches the loan.
    // ONLY for financed deals — a CASH deal hides the Financing section, but the
    // form still hydrates purchase_price/term from the deal, so without this
    // deal_type guard a no-edit Save on a CASH deal would silently overwrite its
    // stored amount_financed/monthly_payment. Financed and monthly are gated
    // separately: a term-less deal still gets a correct Financed Amount, but no
    // fabricated Monthly Payment. The server recomputes both from the
    // canonical engine on save regardless (services.deal_persistence.
    // persist_deal_financials) — these are best-effort echoes for continuity,
    // not the source of truth.
    const { financed, financedComputable } = computeFinanced(form);
    if (deal && isFinancedDeal(deal.deal_type)) {
      if (financedComputable) payload.amount_financed = financed;
      if (monthlyComputable(form) && monthlyPreview !== null)
        payload.monthly_payment = monthlyPreview;
    }
    // Optional string fields: send when non-empty.
    if (form.lender_name?.trim()) payload.lender_name = form.lender_name.trim();
    if (form.credit_tier) payload.credit_tier = form.credit_tier;
    if (form.deal_score) payload.deal_score = form.deal_score;
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await onSave(buildPayload());
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save deal');
    } finally {
      setSaving(false);
    }
  };

  if (!deal) return null;

  const isFinanced = isFinancedDeal(deal.deal_type);
  const statusOptions =
    form.deal_status && !MANUAL_STATUSES.includes(form.deal_status)
      ? [form.deal_status, ...MANUAL_STATUSES]
      : MANUAL_STATUSES;
  const busy = saving || loading;
  const { financed, financedComputable } = computeFinanced(form);
  const monthlyIsComputable = monthlyComputable(form);

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={`Edit Deal #${deal.deal_number}`}
      className="modal-scroll"
      closeOnEscape={!busy}
      closeOnOutsideClick={!busy}
    >
      <CompactForm onSubmit={handleSubmit} layout="vertical" gap="sm" responsive={true}>
        <p className="text-xs text-gray-500 -mt-2 mb-1">
          Financed Amount &amp; Monthly Payment recalculate automatically from Price, Down Payment,
          Tax, Fees, APR and Term. Monthly Payment needs a Term — enter one to calculate it. Deal
          type ({formatDealType(deal.deal_type)}) and number are fixed.
        </p>

        {/* Status */}
        <FormFieldGroup title="Status" defaultExpanded={true}>
          <FormField label="Deal Status">
            <select
              name="deal_status"
              value={form.deal_status || ''}
              onChange={handleChange}
              className="form-input"
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FormField>
        </FormFieldGroup>

        {/* Pricing */}
        <CollapsibleSection title="Pricing" defaultExpanded={true} variant="bordered">
          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="Purchase Price">
              <input
                type="number"
                name="purchase_price"
                value={form.purchase_price || ''}
                onChange={handleChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </FormField>
            <FormField label="Total Vehicle Cost">
              <input
                type="number"
                name="total_vehicle_cost"
                value={form.total_vehicle_cost || ''}
                onChange={handleChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </FormField>
          </FormFieldGroup>
          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="Front Gross">
              <input
                type="number"
                name="front_gross"
                value={form.front_gross || ''}
                onChange={handleChange}
                className="form-input"
                step="0.01"
              />
            </FormField>
            <FormField label="Backend Gross">
              <input
                type="number"
                name="backend_gross"
                value={form.backend_gross || ''}
                onChange={handleChange}
                className="form-input"
                step="0.01"
              />
            </FormField>
            <FormField label="Total Gross">
              <input
                type="number"
                name="total_gross"
                value={form.total_gross || ''}
                onChange={handleChange}
                className="form-input"
                step="0.01"
              />
            </FormField>
          </FormFieldGroup>
          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="Sales Tax">
              <input
                type="number"
                name="sales_tax"
                value={form.sales_tax || ''}
                onChange={handleChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </FormField>
            <FormField label="Total Fees">
              <input
                type="number"
                name="total_fees"
                value={form.total_fees || ''}
                onChange={handleChange}
                className="form-input"
                step="0.01"
                min="0"
              />
            </FormField>
          </FormFieldGroup>
        </CollapsibleSection>

        {/* Financing — relevant for non-CASH deals */}
        {isFinanced && (
          <CollapsibleSection title="Financing" defaultExpanded={true} variant="bordered">
            <FormFieldGroup layout="horizontal" equalWidth={true}>
              <FormField label="Down Payment">
                <input
                  type="number"
                  name="down_payment_amount"
                  value={form.down_payment_amount || ''}
                  onChange={handleChange}
                  className="form-input"
                  step="0.01"
                  min="0"
                />
              </FormField>
              <FormField label="APR (%)">
                <input
                  type="number"
                  name="interest_rate"
                  value={form.interest_rate || ''}
                  onChange={handleChange}
                  className="form-input"
                  step="0.01"
                  min="0"
                  max="50"
                />
              </FormField>
              <FormField label="Term (months)">
                <input
                  type="number"
                  name="term_months"
                  value={form.term_months || ''}
                  onChange={handleChange}
                  className="form-input"
                  step="1"
                  min="0"
                />
              </FormField>
            </FormFieldGroup>

            {/* Derived — read-only, recompute live (matches the BHPH loan formula) */}
            <FormFieldGroup layout="horizontal" equalWidth={true}>
              <FormField label="Financed Amount (calculated)">
                <input
                  type="text"
                  name="amount_financed"
                  value={financedComputable ? formatCurrency(financed) : '—'}
                  readOnly
                  tabIndex={-1}
                  className="form-input bg-gray-100 text-gray-700 cursor-not-allowed"
                />
              </FormField>
              <FormField label="Monthly Payment (calculated)">
                <input
                  type="text"
                  name="monthly_payment"
                  value={
                    !monthlyIsComputable
                      ? '—'
                      : monthlyPreviewLoading && monthlyPreview === null
                        ? 'Calculating…'
                        : monthlyPreview !== null
                          ? formatCurrency(monthlyPreview)
                          : '—'
                  }
                  readOnly
                  tabIndex={-1}
                  className="form-input bg-gray-100 text-gray-700 cursor-not-allowed"
                />
              </FormField>
            </FormFieldGroup>

            <FormField label="Lender Name">
              <input
                type="text"
                name="lender_name"
                value={form.lender_name || ''}
                onChange={handleChange}
                className="form-input"
              />
            </FormField>
          </CollapsibleSection>
        )}

        {/* Credit */}
        <CollapsibleSection title="Credit Tier" defaultExpanded={false} variant="bordered">
          <FormFieldGroup layout="horizontal" equalWidth={true}>
            <FormField label="Credit Score">
              <input
                type="number"
                name="credit_score"
                value={form.credit_score || ''}
                onChange={handleChange}
                className="form-input"
                step="1"
                min="300"
                max="850"
              />
            </FormField>
            <FormField label="Credit Tier">
              <select
                name="credit_tier"
                value={form.credit_tier || ''}
                onChange={handleChange}
                className="form-input"
              >
                {CREDIT_TIERS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Deal Score">
              <select
                name="deal_score"
                value={form.deal_score || ''}
                onChange={handleChange}
                className="form-input"
              >
                {DEAL_SCORES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </FormField>
          </FormFieldGroup>
        </CollapsibleSection>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </CompactForm>
    </ResponsiveModal>
  );
};

export default EditDealModal;
